// Library Methods
import { useEffect, useMemo, useRef, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { PauseIcon, PlayIcon } from "src/assets/icons";
import CustomSlider from "src/components/shared/inputs/CustomSlider/CustomSlider";

// Types
import { IndividualExternalExamType } from "src/services/exams/types";

type SliderImage = IndividualExternalExamType["images"][number];

type ComparisonImageSliderType = {
  images: SliderImage[];
  selectedImageId: string | undefined;
  onSelect: (image: SliderImage) => void;
  compact?: boolean; // hides the frame counter
};

// How long each frame stays on screen while auto-playing.
const PLAYBACK_INTERVAL_MS = 50;

const ComparisonImageSlider = ({
  images,
  selectedImageId,
  onSelect,
  compact = false,
}: ComparisonImageSliderType) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const currentIndex = useMemo(
    () => images.findIndex((image) => image.id === selectedImageId),
    [images, selectedImageId]
  );

  // Read through a ref rather than depending on `onSelect` directly -- the
  // parent recreates that callback on every render (e.g. on every scroll
  // tick while panning the detail image), which would otherwise reset the
  // playback timer before it ever gets a chance to fire.
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  // Stop playback whenever the underlying image set changes (e.g. a
  // different exam is now being shown through the same mounted component).
  useEffect(() => {
    setIsPlaying(false);
  }, [images]);

  useEffect(() => {
    if (!isPlaying || currentIndex === -1) return;

    if (currentIndex >= images.length - 1) {
      setIsPlaying(false);
      return;
    }

    const timeout = setTimeout(() => {
      onSelectRef.current(images[currentIndex + 1]);
    }, PLAYBACK_INTERVAL_MS);

    return () => clearTimeout(timeout);
  }, [isPlaying, currentIndex, images]);

  const togglePlay = () => {
    // Replaying from the end should start over from the beginning instead
    // of doing nothing (there's nowhere further to advance to).
    if (!isPlaying && currentIndex >= images.length - 1) {
      onSelect(images[0]);
    }
    setIsPlaying((prev) => !prev);
  };

  const handleScrub = (value: number) => {
    setIsPlaying(false);
    onSelect(images[value]);
  };

  // Arrow keys step through images one at a time -- holding the key down
  // relies on the browser's native keydown auto-repeat to keep advancing.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    if (currentIndex === -1) return;

    event.preventDefault();
    setIsPlaying(false);

    const nextIndex =
      event.key === "ArrowRight"
        ? Math.min(currentIndex + 1, images.length - 1)
        : Math.max(currentIndex - 1, 0);

    if (nextIndex !== currentIndex) {
      onSelect(images[nextIndex]);
    }
  };

  return (
    <Box
      tabIndex={0}
      onKeyDown={handleKeyDown}
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        padding: "8px",
        boxSizing: "border-box",
        outline: "none",
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Box
        onClick={togglePlay}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "20px",
          height: "20px",
          borderRadius: "2px",
          backgroundColor: "#5B6D7A",
          color: "#FFF",
          cursor: "pointer",
          flexShrink: 0,
          mr: "16px",
        }}
      >
        {isPlaying ? (
          <PauseIcon height="16px" width="16px" />
        ) : (
          <PlayIcon height="16px" width="16px" />
        )}
      </Box>

      <Box sx={{ flex: 1, display: "flex" }}>
        <CustomSlider
          min={0}
          max={Math.max(images.length - 1, 0)}
          step={1}
          value={Math.max(currentIndex, 0)}
          onChange={handleScrub}
        />
      </Box>

      {!compact && (
        <Typography
          sx={{ color: "#FFF", fontSize: "12px", flexShrink: 0 }}
        >{`${Math.max(currentIndex, 0) + 1} / ${images.length}`}</Typography>
      )}
    </Box>
  );
};

export default ComparisonImageSlider;
